import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'wnc_inline_edit_mode';

const InlineEditContext = createContext(null);

/**
 * useInlineEdit — access edit-mode state + save router from any component.
 * Returns { enabled, isAdmin, toggle, saveField, publishAll, dirty, saving }.
 */
export const useInlineEdit = () => {
  const ctx = useContext(InlineEditContext);
  if (!ctx) return { enabled: false, isAdmin: false, toggle: () => {}, saveField: () => {}, dirty: false, saving: false, publishAll: () => {}, dirtyScopes: new Set() };
  return ctx;
};

/**
 * InlineEditProvider — wraps the public site. Manages:
 *  - `enabled`: whether the admin has clicked "Edit Mode" in the PreviewBanner
 *  - a save router that dispatches per-field updates to the correct API
 *  - a dirty-scope registry so "Publish Draft" knows which pages to publish
 */
export const InlineEditProvider = ({ children }) => {
  const [enabled, setEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  });
  const [isAdmin, setIsAdmin] = useState(() => !!localStorage.getItem('admin_token'));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dirtyScopesRef = useRef(new Set());

  useEffect(() => {
    setIsAdmin(!!localStorage.getItem('admin_token'));
  }, []);

  const authHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      if (next) sessionStorage.setItem(STORAGE_KEY, '1');
      else sessionStorage.removeItem(STORAGE_KEY);
      return next;
    });
  }, []);

  const markDirty = useCallback((scope) => {
    dirtyScopesRef.current.add(scope);
    setDirty(true);
  }, []);

  /**
   * saveField — the single entry point every EditableText/RichText/Cta uses.
   * @param scope object describing what to save: { type, ...ids }
   *   Supported scopes:
   *     { type: 'homepage_section', sectionId, path, value }         → draft_content merge
   *     { type: 'global', field, value }                             → PUT /api/content/global
   *     { type: 'sponsorship', path, value }                         → PUT /api/content/sponsorship
   *     { type: 'program', path, value }                             → PUT /api/content/program
   *     { type: 'golf', path, value }                                → PUT /api/content/golf
   *     { type: 'speaker', speakerId, field, value }                 → PUT /api/speakers/{id}
   */
  const saveField = useCallback(async (scope) => {
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...authHeaders() };
      let res;
      if (scope.type === 'homepage_section') {
        // Fetch current section, merge new path into draft_content
        const r = await fetch(`${API_URL}/api/homepage/sections/${scope.sectionId}`, { headers });
        if (!r.ok) throw new Error('section fetch failed');
        const section = await r.json();
        const draft = { ...(section.draft_content || section.content || {}) };
        setDeep(draft, scope.path, scope.value);
        res = await fetch(`${API_URL}/api/homepage/sections/${scope.sectionId}`, {
          method: 'PUT', headers, body: JSON.stringify({ draft_content: draft }),
        });
        markDirty('homepage');
      } else if (scope.type === 'global') {
        res = await fetch(`${API_URL}/api/content/global`, {
          method: 'PUT', headers, body: JSON.stringify({ [scope.field]: scope.value }),
        });
        // Global has no draft concept — direct save, but still mark for user awareness
      } else if (scope.type === 'sponsorship' || scope.type === 'program' || scope.type === 'golf') {
        // Non-homepage pages use the generic content_drafts collection.
        const key = scope.type;
        // Fetch current draft; if none, snapshot from live first.
        let draftBody = {};
        try {
          const r = await fetch(`${API_URL}/api/content/drafts/${key}`, { headers });
          if (r.ok) draftBody = (await r.json())?.data || {};
          else if (r.status === 404) {
            // Snapshot from live so subsequent edits merge cleanly
            const snap = await fetch(`${API_URL}/api/content/drafts/${key}/snapshot-from-live`, { method: 'POST', headers });
            if (snap.ok) {
              const r2 = await fetch(`${API_URL}/api/content/drafts/${key}`, { headers });
              if (r2.ok) draftBody = (await r2.json())?.data || {};
            }
          }
        } catch { /* fall through with empty draft */ }
        setDeep(draftBody, scope.path, scope.value);
        res = await fetch(`${API_URL}/api/content/drafts/${key}`, {
          method: 'PUT', headers, body: JSON.stringify({ data: draftBody }),
        });
        markDirty(scope.type);
      } else if (scope.type === 'speaker') {
        res = await fetch(`${API_URL}/api/speakers/${scope.speakerId}`, {
          method: 'PUT', headers, body: JSON.stringify({ [scope.field]: scope.value }),
        });
      } else {
        throw new Error(`unknown scope type: ${scope.type}`);
      }
      if (!res.ok) throw new Error(`save failed: ${res.status}`);
      return true;
    } catch (err) {
      console.error('inline save error:', err);
      toast.error('Failed to save — check console');
      return false;
    } finally {
      setSaving(false);
    }
  }, [markDirty]);

  const publishAll = useCallback(async () => {
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...authHeaders() };
      const scopes = dirtyScopesRef.current;
      if (scopes.has('homepage')) {
        await fetch(`${API_URL}/api/homepage/publish`, {
          method: 'POST', headers, body: JSON.stringify({ note: 'Inline editor publish' }),
        });
      }
      // Non-homepage pages use the generic content_drafts publish endpoint per key
      for (const t of ['sponsorship', 'program', 'golf']) {
        if (scopes.has(t)) {
          const key = t;
          await fetch(`${API_URL}/api/content/drafts/${key}/publish`, {
            method: 'POST', headers,
          });
        }
      }
      dirtyScopesRef.current.clear();
      setDirty(false);
      toast.success('Draft published — changes are now live');
      // Force a reload so the newly-published sections replace the drafts in memory
      window.location.reload();
    } catch (err) {
      toast.error('Publish failed');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }, []);

  const value = useMemo(() => ({
    enabled: enabled && isAdmin,
    isAdmin,
    toggle,
    saveField,
    publishAll,
    dirty,
    saving,
  }), [enabled, isAdmin, toggle, saveField, publishAll, dirty, saving]);

  return <InlineEditContext.Provider value={value}>{children}</InlineEditContext.Provider>;
};

// -------- Helpers --------
function setDeep(obj, path, value) {
  const parts = Array.isArray(path) ? path : String(path).split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    if (cur[k] == null || typeof cur[k] !== 'object') cur[k] = /^\d+$/.test(parts[i + 1]) ? [] : {};
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = value;
  return obj;
}
